<template>
  <div>
    <v-card class="mb-4 scrollable-card" :style="cardStyle">
      <v-card-title class="d-flex justify-space-between align-center">
        <div class="d-flex align-center ga-4">
          <span class="card-title">{{ props.title }}</span>
          <!-- Slot for custom controls -->
          <slot name="controls" :selectedItem="selectedItem" :updateSelection="updateSelection">
            <!-- Default content if no slot provided -->
          </slot>
        </div>
        <div class="breadcrumbs-container d-flex align-center" v-if="!$slots.controls">
          <v-breadcrumbs 
            :items="breadcrumbs" 
            divider=">" 
            @click="handleBreadcrumbClick"
            class="text-subtitle-2 pa-0 breadcrumbs-wrapper"
            density="compact"
          ></v-breadcrumbs>
        </div>
        
        <!-- Navigation controls when controls slot exists -->
        <div class="navigation-controls d-flex align-center" v-else>
          <v-btn
            size="small"
            icon="mdi-chevron-up"
            :disabled="isAtTopLevel"
            @click="navigateUp"
            density="comfortable"
            variant="text"
            class="mr-1"
          ></v-btn>
          <v-btn
            size="small"
            icon="mdi-chevron-down"
            :disabled="isAtBottomLevel"
            @click="navigateDown"
            density="comfortable"
            variant="text"
          ></v-btn>
        </div>
      </v-card-title>
      <v-card-text>
        <v-chart class="chart" ref="chartRef" :style="{ height: chartHeight + 'px' }" :option="chartOption" @click="handleChartClick" />
        
        <!-- Pagination controls - only shown when needed -->
        <div v-if="totalPages > 1" class="pagination-controls mt-2">
          <div class="d-flex align-center justify-space-between">
            <div class="text-body-2">
              {{ t('showing') }} {{ startItem }}-{{ endItem }} {{ t('of') }} {{ totalItems }} {{ t('items') }}
            </div>
            <div>
              <v-btn 
                size="small" 
                icon="mdi-chevron-left" 
                :disabled="currentPage === 1" 
                @click="changePage(currentPage - 1)"
                density="comfortable"
              ></v-btn>
              
              <span class="mx-2">{{ currentPage }} / {{ totalPages }}</span>
              
              <v-btn 
                size="small" 
                icon="mdi-chevron-right" 
                :disabled="currentPage === totalPages" 
                @click="changePage(currentPage + 1)"
                density="comfortable"
              ></v-btn>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>
    
  <script setup>
  import { ref, onMounted, computed, watch, defineProps } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { use } from "echarts/core";
  import { CanvasRenderer } from "echarts/renderers";
  import { BoxplotChart, ScatterChart } from "echarts/charts";
  import { TooltipComponent, LegendComponent, TitleComponent, GridComponent, DataZoomComponent } from "echarts/components";
  import VChart from "vue-echarts";
  import { formatTimeValue } from '@/utils/chartUtils';
  
  use([CanvasRenderer, BoxplotChart, ScatterChart, TooltipComponent, LegendComponent, TitleComponent, GridComponent, DataZoomComponent]);
  
  const props = defineProps({
    title: {
      type: String,
      default: ''
    },
    chartFontColor: {
      type: String,
      default: '#000000'
    },
    boxColor: {
      type: String,
      default: '#b8c5f2'
    },
    chartCardHeight: {
      type: Number,
      default: 460
    },
    currentLevel: {
      type: Number,
      default: 0
    },
    dataProviders: {
      type: Object,
      required: true
    },
    contextUpdaters: {
      type: Object,
      required: true
    },
    navigationConfig: {
      type: Array,
    },
    filterContext: {
      type: Object,
      required:true
    },
    // Only present in the issue dashboard, which has a status-history grain.
    // Optional here: it is watched purely as a refresh trigger.
    filterStatusHistoryContext: {
      type: Object,
      default: null
    },
    dataLoaded: {
      type: Boolean,
      default: false
    }
  });
  
  const { t } = useI18n();
  
  const chartRef = ref(null);
  const chartOption = ref({});
  const chartHeight = ref(props.chartCardHeight);
  const currentLevel = ref(props.currentLevel);
  const currentParent = ref(null);
  const navigationState = ref({}); // Stores state for each level
  const breadcrumbPath = ref([]);
  const topLevelIndex = ref(0); // First navigation level that actually has data
  
  // Pagination state
  const currentPage = ref(1);
  const itemsPerPage = ref(20); // Maximum number of items to show at once
  const totalItems = ref(0);
  const fullDataset = ref([]); // Store the complete dataset
  const fullDetailData = ref([]); // Store detail scatter data
  
  // Computed properties for pagination
  const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));
  const startItem = computed(() => (currentPage.value - 1) * itemsPerPage.value + 1);
  const endItem = computed(() => Math.min(currentPage.value * itemsPerPage.value, totalItems.value));
  
  const selectedItem = ref(null);

  const updateSelection = (newSelection) => {
    selectedItem.value = newSelection;
    // Emit event to parent if needed
    emit('selectionChanged', newSelection);
  };
  const emit = defineEmits(['selectionChanged', 'updateFilter']);

  // Navigation level computed properties
  const isAtTopLevel = computed(() => {
    const currentLevelIndex = props.navigationConfig.findIndex(config => config.level === currentLevel.value);
    return currentLevelIndex === topLevelIndex.value;
  });

  const isAtBottomLevel = computed(() => {
    const currentLevelIndex = props.navigationConfig.findIndex(config => config.level === currentLevel.value);
    return currentLevelIndex >= props.navigationConfig.length - 1;
  });

  // Navigation methods
  const navigateUp = async () => {
    if (isAtTopLevel.value) return;

    const currentLevelIndex = props.navigationConfig.findIndex(config => config.level === currentLevel.value);

    // Going up to the top level: re-resolve which level has data and reset.
    if (currentLevelIndex - 1 === topLevelIndex.value) {
      await resetToTopLevel();
      return;
    }

    const prevLevelConfig = props.navigationConfig[currentLevelIndex - 1];

    // Go back to previous level
    currentLevel.value = prevLevelConfig.level;

    // Truncate breadcrumb path. Breadcrumbs start at topLevelIndex, so offset
    // the nav-config index into the breadcrumb array.
    breadcrumbPath.value = breadcrumbPath.value.slice(0, currentLevelIndex - topLevelIndex.value);

    // Restore previous level state
    const levelState = navigationState.value[prevLevelConfig.level];
    currentParent.value = levelState?.parentId || null;

    // Refresh data for current level
    await updateChartBasedOnCurrentLevel();
  };

  const navigateDown = async () => {
    if (isAtBottomLevel.value) return;
    
    const currentLevelIndex = props.navigationConfig.findIndex(config => config.level === currentLevel.value);
    const nextLevelConfig = props.navigationConfig[currentLevelIndex + 1];
    
    // Move to next level
    currentLevel.value = nextLevelConfig.level;
    
    // Update breadcrumb - add a generic title for the next level
    const newBreadcrumb = { 
      title: t(nextLevelConfig.titleKey), 
      disabled: false, 
      href: '#',
      level: nextLevelConfig.level
    };
    breadcrumbPath.value.push(newBreadcrumb);
    
    // Refresh data for the new level
    await updateChartBasedOnCurrentLevel();
  };

  // Change page function
  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages.value) {
      currentPage.value = newPage;
      updateChartWithPagination();
    }
  };
  
  
  // Apply pagination to current dataset and update chart
  const updateChartWithPagination = () => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = Math.min(start + itemsPerPage.value, totalItems.value);
    const paginatedData = fullDataset.value.slice(start, end);
    const paginatedDetailData = fullDetailData.value.filter(item => {
      const itemIndex = fullDataset.value.findIndex(d => d.name === item.category);
      return itemIndex >= start && itemIndex < end;
    });
    
    // Update chart with paginated data
    updateChartOption(paginatedData, paginatedDetailData, true);
  };
  
  const cardStyle = computed(() => {
    return {
      'max-height': `${props.chartCardHeight}px`,
      'min-height': '460px',
      'overflow-y': 'auto',
      'height': 'auto',
      'display': 'flex',
      'flex-direction': 'column'
    };
  });
  
  // Initialize breadcrumb path based on navigation config
  const initializeBreadcrumbs = () => {
    const topConfig = props.navigationConfig[topLevelIndex.value];
    breadcrumbPath.value = [{
      title: t(topConfig.titleKey),
      disabled: false,
      href: '#',
      level: topConfig.level
    }];
  };
  
  const breadcrumbs = computed(() => {
    return breadcrumbPath.value.map((item, index) => ({
      ...item,
      disabled: index === breadcrumbPath.value.length - 1
    }));
  });
  
  const updateChartOption = (data, isPaginated = false) => {
  // If this is the first call with the full dataset, store it and apply pagination
  if (!isPaginated) {
    // DuckDB returns quantiles as DECIMAL, which Arrow hands back as a string
    // or BigInt - coerce every stat before sorting or plotting, or the box
    // values and the median sort below silently break.
    const toNumber = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };
    data = data.map((item) => ({
      ...item,
      count: toNumber(item.count),
      min: toNumber(item.min),
      Q1: toNumber(item.Q1),
      median: toNumber(item.median),
      Q3: toNumber(item.Q3),
      max: toNumber(item.max),
      average: toNumber(item.average),
      standard_deviation: toNumber(item.standard_deviation),
    }));

    // Sort by median value (descending)
    const processedData = data.sort((a, b) => b.median - a.median);
    
    fullDataset.value = [...processedData];
    totalItems.value = processedData.length;
    
    // No detail data needed since SQL provides aggregated stats
    fullDetailData.value = [];
    
    // Reset to first page when new data is loaded
    currentPage.value = 1;
    
    // If data exceeds our limit, apply pagination immediately
    if (processedData.length > itemsPerPage.value) {
      const paginatedData = processedData.slice(0, itemsPerPage.value);
      updateChartOption(paginatedData, [], true);
      return;
    } else {
      data = processedData;
    }
  }

  // Constants for chart sizing and thresholds
  const BOX_HEIGHT = 25;
  const BOX_GAP = 8;
  const AXIS_MARGIN = 120;
  const MIN_CHART_HEIGHT = 100;
  const ANIMATION_THRESHOLD = 30;
  
  const categories = data.map(item => item.title);

  // Calculate height before setting options
  const calculatedHeight = Math.max(
    MIN_CHART_HEIGHT,
    (categories.length * BOX_HEIGHT) + 
    ((categories.length - 1) * BOX_GAP) + 
    AXIS_MARGIN
  );

  // Update height
  chartHeight.value = calculatedHeight;
  
  // Determine if we should use animation
  const shouldAnimate = categories.length <= ANIMATION_THRESHOLD;
  
  // Prepare boxplot data using SQL query results
  const boxplotData = data.map(item => ({
    name: item.title,
    value: [item.min, item.Q1, item.median, item.Q3, item.max]
  }));
  
  // Build the chart options
  const newChartOptions = {
    animation: shouldAnimate,
    animationDuration: shouldAnimate ? 800 : 0,
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params) => {
        if (params.componentSubType === 'boxplot') {
          const [min, Q1, median, Q3, max] = params.value;
          const dataItem = data.find(d => d.title === params.name);
          return `
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.4;">
              <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e0e0e0;">
                ${params.name}
              </div>
              <div style="margin-bottom: 8px;">
                <div style="color: #666; font-size: 11px; text-transform: uppercase; font-weight: 500; margin-bottom: 4px;">Sample Size</div>
                <div style="font-weight: 500;">${dataItem ? dataItem.count : 'N/A'}</div>
              </div>
              <div style="margin-bottom: 8px;">
                <div style="color: #666; font-size: 11px; text-transform: uppercase; font-weight: 500; margin-bottom: 4px;">Distribution</div>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px;">
                  <div><span style="color: #666;">Max:</span> <strong>${formatTimeValue(max)}</strong></div>
                  <div><span style="color: #666;">Q3:</span> <strong>${formatTimeValue(Q3)}</strong></div>
                  <div><span style="color: #666;">Median:</span> <strong style="color: #1976d2;">${formatTimeValue(median)}</strong></div>
                  <div><span style="color: #666;">Q1:</span> <strong>${formatTimeValue(Q1)}</strong></div>
                  <div><span style="color: #666;">Min:</span> <strong>${formatTimeValue(min)}</strong></div>
                </div>
              </div>
              <div>
                <div style="color: #666; font-size: 11px; text-transform: uppercase; font-weight: 500; margin-bottom: 4px;">Statistics</div>
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px;">
                  <div><span style="color: #666;">Average:</span> <strong>${formatTimeValue(dataItem ? dataItem.average : 'N/A')}</strong></div>
                  <div><span style="color: #666;">Std Dev:</span> <strong>${formatTimeValue(dataItem ? dataItem.standard_deviation : 'N/A')}</strong></div>
                </div>
              </div>
            </div>
          `;
        }
        return '';
      }
    },
    grid: {
      left: '3%',
      right: '4%', 
      bottom: '60px',
      top: '10px',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      name: t('durationDays'),
      nameLocation: 'middle',
      nameGap: 30,
      scale: true,
      axisLabel: { 
        color: props.chartFontColor,
        formatter: (value) => formatTimeValue(value)
      }
    },
    yAxis: {
      type: 'category',
      data: categories,
      inverse: true,
      axisTick: { alignWithLabel: true },
      axisLine: { show: false },
      axisLabel: {
        margin: 10,
        color: props.chartFontColor,
        fontSize: 12,
        overflow: 'truncate',
        width: 120
      }
    },
    series: [
      {
        name: 'boxplot',
        type: 'boxplot',
        data: boxplotData,
        itemStyle: {
          color: props.boxColor,
          borderColor: props.boxColor ? adjustColorShade(props.boxColor, -20) : '#7a9bcc'
        },
        boxWidth: [BOX_HEIGHT * 0.5, BOX_HEIGHT * 0.8],
        tooltip: {
          formatter: function(params) {
            const [min, Q1, median, Q3, max] = params.value;
            const dataItem = data.find(d => d.title === params.name);
            return `
              <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.4;">
                <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid #e0e0e0;">
                  ${params.name}
                </div>
                <div style="margin-bottom: 8px;">
                  <div style="color: #666; font-size: 11px; text-transform: uppercase; font-weight: 500; margin-bottom: 4px;">Sample Size</div>
                  <div style="font-weight: 500;">${dataItem ? dataItem.count : 'N/A'}</div>
                </div>
                <div style="margin-bottom: 8px;">
                  <div style="color: #666; font-size: 11px; text-transform: uppercase; font-weight: 500; margin-bottom: 4px;">Distribution</div>
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px;">
                    <div><span style="color: #666;">Max:</span> <strong>${formatTimeValue(max)}</strong></div>
                    <div><span style="color: #666;">Q3:</span> <strong>${formatTimeValue(Q3)}</strong></div>
                    <div><span style="color: #666;">Median:</span> <strong style="color: #1976d2;">${formatTimeValue(median)}</strong></div>
                    <div><span style="color: #666;">Q1:</span> <strong>${formatTimeValue(Q1)}</strong></div>
                    <div><span style="color: #666;">Min:</span> <strong>${formatTimeValue(min)}</strong></div>
                  </div>
                </div>
                <div>
                  <div style="color: #666; font-size: 11px; text-transform: uppercase; font-weight: 500; margin-bottom: 4px;">Statistics</div>
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px;">
                    <div><span style="color: #666;">Average:</span> <strong>${formatTimeValue(dataItem ? dataItem.average : 'N/A')}</strong></div>
                    <div><span style="color: #666;">Std Dev:</span> <strong>${formatTimeValue(dataItem ? dataItem.standard_deviation : 'N/A')}</strong></div>
                  </div>
                </div>
              </div>
            `;
          }
        }
      }
    ]
  };

  // Update the chart option
  chartOption.value = newChartOptions;

  // Handle resize after a short delay to ensure DOM is updated
  setTimeout(() => {
    if (chartRef.value && chartRef.value.resize) {
      try {
        chartRef.value.resize();
      } catch (error) {
        console.warn("Error resizing chart:", error);
      }
    }
  }, 200);
};
  
  // Handle chart item clicks for drilling down
  const handleChartClick = async (params) => {
    // Get the category name from the click
    let categoryName;
    if (params.componentSubType === 'boxplot') {
      categoryName = params.name;
    } else if (params.componentSubType === 'scatter') {
      categoryName = params.value[1];
    } else {
      return;
    }
    
    // Find the actual data item
    //const actualItem = fullDataset.value.find(item => item.name === categoryName);
    
    if (!categoryName) return;
    
    // Find current level index in navigation config
    const currentLevelIndex = props.navigationConfig.findIndex(config => config.level === currentLevel.value);
    
    // If we're at the last level, there's nowhere to drill down
    if (currentLevelIndex >= props.navigationConfig.length - 1) {
      return;
    }
    
    // Get the next level in the navigation hierarchy
    const nextLevelConfig = props.navigationConfig[currentLevelIndex + 1];
    
    // Store relevant information for this level before changing
    navigationState.value[currentLevel.value] = {
      parentId: currentParent.value,
      selectedName: categoryName
    };
    
    // Find the appropriate context updater for the current level
    const currentLevelData = props.dataProviders[currentLevel.value];
    const filterContextUpdater = props.contextUpdaters[currentLevel.value];
    const navigationConfigLevel = props.navigationConfig.find(nav => nav.level === currentLevel.value);
    
    // Update current level and parent references before updating filterContext
    currentLevel.value = nextLevelConfig.level;
    currentParent.value = navigationState.value[props.navigationConfig[currentLevelIndex].level]?.contextParam || null;
    
    // Update breadcrumb path
    const newBreadcrumb = { 
      title: categoryName || t(nextLevelConfig.titleKey), 
      disabled: false, 
      href: '#',
      level: nextLevelConfig.level
    };    
    breadcrumbPath.value.push(newBreadcrumb);  
  
    if (currentLevelData && filterContextUpdater && navigationConfigLevel) {
      const levelData = await currentLevelData();          
      const selectedItem = levelData.find(item => item.title === categoryName);
      const contextParam = selectedItem?.title;
      const parentIdParam = selectedItem?.parentId ? selectedItem.parentId : null;
      //const typeParam = selectedItem?.type ? selectedItem.type : null;
  
      await filterContextUpdater(contextParam, parentIdParam/*, typeParam*/); //this will trigger refresh
    }
  };
  
  const handleBreadcrumbClick = async (e) => {
    const clickedBreadcrumb = breadcrumbPath.value.find(breadcrumb => breadcrumb.title === e.target.text);
    
    if (!clickedBreadcrumb) return;
    
    // Find the level in the navigation config
    const levelIndex = props.navigationConfig.findIndex(config => config.level === clickedBreadcrumb.level);
    
    if (levelIndex > topLevelIndex.value) {
      // Get the corresponding level config
      const levelConfig = props.navigationConfig[levelIndex];

      // Restore the state for this level if available
      const levelState = navigationState.value[levelConfig.level];
      currentLevel.value = levelConfig.level;
      currentParent.value = levelState?.parentId || null;

      // Truncate breadcrumb path to this level. Breadcrumbs start at
      // topLevelIndex, so offset the nav-config index into the breadcrumb array.
      breadcrumbPath.value = breadcrumbPath.value.slice(0, levelIndex - topLevelIndex.value + 1);
  
      //Get data from level above so we know how to filter
      const prevLevelConfig = props.navigationConfig[levelIndex - 1];
      const currentLevelData = props.dataProviders[prevLevelConfig.level];
      const filterContextUpdater = props.contextUpdaters[prevLevelConfig.level];
      
      // Apply the appropriate context update
      if (currentLevelData && filterContextUpdater && levelState?.selectedName) {
        const levelData = await currentLevelData(false /*unfiltered*/);
        // Box-plot rows carry `title`, not `name` - matching on `name` never
        // found the parent, so stepping up a breadcrumb left the old filter.
        const selectedItem = levelData.find(item => item.title === clickedBreadcrumb.title);
        const contextParam = selectedItem?.title;
        const parentIdParam = selectedItem?.parentId ? selectedItem.parentId : null;
        const typeParam = selectedItem?.type ? selectedItem.type : null;
  
        await filterContextUpdater(contextParam, parentIdParam, typeParam);
      }
    } else {
      resetToTopLevel();
    }
  };
  
  const resetToTopLevel = async () => {
    const resetContext = props.contextUpdaters['reset'];
    if (resetContext) {
      resetContext();
    }

    // Re-resolve the top level (skipping any empty leading levels) and render.
    await renderTopLevel();
  };

  // Render the chart at the top of the hierarchy, automatically skipping any
  // leading levels that have no data (e.g. a project that never populates
  // issueCategory). Falls back to the first level if nothing has data anywhere.
  const renderTopLevel = async () => {
    currentPage.value = 1;
    for (let i = 0; i < props.navigationConfig.length; i++) {
      const level = props.navigationConfig[i].level;
      const dataProvider = props.dataProviders[level];
      if (!dataProvider) continue;
      const data = await dataProvider();
      if (data && data.length > 0) {
        topLevelIndex.value = i;
        currentLevel.value = level;
        currentParent.value = null;
        navigationState.value = {};
        initializeBreadcrumbs();
        updateChartOption(data);
        return;
      }
    }
    // Nothing has data at any level — show the first level (empty).
    topLevelIndex.value = 0;
    currentLevel.value = props.navigationConfig[0].level;
    currentParent.value = null;
    navigationState.value = {};
    initializeBreadcrumbs();
    updateChartOption([]);
  };
  
  const updateChartBasedOnCurrentLevel = async () => {
    // Reset pagination when updating based on current level
    currentPage.value = 1;
    
    // Get the appropriate data provider for the current level
    const dataProvider = props.dataProviders[currentLevel.value];
    
    if (!dataProvider) {
      console.error(`No data provider found for level: ${currentLevel.value}`);
      return;
    }
  
    const data = await dataProvider();
    updateChartOption(data);
  };
  
  watch(
    () =>
      [
        props.filterContext.filtersLastUpdated,
        props.filterStatusHistoryContext?.filtersLastUpdated
      ],
    () => {
      if (!isMounted.value) return;
      // At the top of the hierarchy, re-resolve which level has data; once the
      // user has drilled in, just refresh the level they're currently on.
      if (breadcrumbPath.value.length <= 1) {
        renderTopLevel();
      } else {
        updateChartBasedOnCurrentLevel();
      }
    },
    { deep: true }
  );

  // Watch for changes in chart font color
  watch(() => props.chartFontColor, () => {
    updateChartBasedOnCurrentLevel();
  });
  
  // Watch for changes in box color
  watch(() => props.boxColor, () => {
    updateChartBasedOnCurrentLevel();
  });
  
  watch(() => props.currentLevel, (newVal) => {
    currentLevel.value = newVal;
  });
  
  // Watch for page changes to update the chart
  watch(() => currentPage.value, () => {
    updateChartWithPagination();
  });
  
  // Helper function to adjust color shade (darken/lighten)
  const adjustColorShade = (color, percent) => {
    if (!color) return '#7a9bcc';
    
    // If color is in hex format
    let R, G, B;
    if (color.startsWith('#')) {
      const hex = color.substring(1);
      R = parseInt(hex.substring(0, 2), 16);
      G = parseInt(hex.substring(2, 4), 16);
      B = parseInt(hex.substring(4, 6), 16);
    } 
    // If color is in rgb format
    else if (color.startsWith('rgb')) {
      const rgb = color.match(/\d+/g);
      if (rgb && rgb.length >= 3) {
        R = parseInt(rgb[0]);
        G = parseInt(rgb[1]);
        B = parseInt(rgb[2]);
      } else {
        return '#7a9bcc'; // Fallback color
      }
    } else {
      return '#7a9bcc'; // Fallback color
    }
    
    // Adjust the color
    R = Math.max(0, Math.min(255, R + percent));
    G = Math.max(0, Math.min(255, G + percent));
    B = Math.max(0, Math.min(255, B + percent));
    
    // Convert back to hex
    return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
  };
  
  const isMounted = ref(false);
  
  const initializeChart = async () => {
    isMounted.value = true;
    // Render the top level, skipping any leading levels that have no data.
    await renderTopLevel();
  };

  onMounted(async () => {
    if (props.dataLoaded) {
      await initializeChart();
    }
  });

  // Watch for dataLoaded changes
  watch(() => props.dataLoaded, async (newValue) => {
    if (newValue && !isMounted.value) {
      await initializeChart();
    }
  });
  </script>
  
  <style scoped>
  .chart {
    width: 100%;
    height: 100%;
  }
  
  .scrollable-card {
    display: flex;
    flex-direction: column;
    max-height: v-bind('props.chartCardHeight + "px"');
    overflow-y: auto !important; /* Force scrollbar when needed */
  }
  
  .scrollable-card :deep(.v-card-text) {
    overflow-y: visible; /* Allow content to determine scroll */
    flex: 1;
    padding-top:0;
    padding-bottom: 16px; /* Ensure some padding at bottom */
  }
  
  .card-title {
    flex: 0 1 auto;
    margin-right: 8px;
  }
  
  .breadcrumbs-container {
    flex: 1 1 auto;
    overflow-x: auto;
    max-width: 70%;
    white-space: nowrap;
    scrollbar-width: thin;
    display: flex;
    justify-content: flex-end;
  }
  
  .breadcrumbs-container::-webkit-scrollbar {
    height: 4px;
  }
  
  .breadcrumbs-container::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 4px;
  }
  
  .breadcrumbs-wrapper {
    margin-left: auto;
  }
  
  .breadcrumbs-wrapper :deep(.v-breadcrumbs-item) {
    white-space: nowrap;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .breadcrumbs-wrapper :deep(.v-breadcrumbs__wrapper) {
    display: flex;
    flex-direction: row-reverse;
  }
  
  .pagination-controls {
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }
  
  .navigation-controls {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  </style>